import React, { useState, useEffect, useCallback } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FlaskConical, Search, Star, Cog, Package, Bug, Leaf } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getBackendURL, getImageUrl } from '../utils/assetUrlHelper';

// API_URL was here

// Category icons mapping
const categoryIcons = {
  'Processing': Package,
  'Cultivation': Leaf,
  'Storage': Package,
  'Packaging': Package,
  'Pest Management': Bug,
  'Propagation': Leaf,
  'Other': Cog
};

// Technology Card Component - defined outside main component
const TechnologyCard = React.memo(({ tech, isPrioritized, onSelect }) => {
  const CategoryIcon = categoryIcons[tech.category] || Cog;
  const imageUrl = getImageUrl(tech.image_url);

  return (
    <div
      onClick={() => onSelect(tech)}
      className={`bg-white border rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer group ${isPrioritized
        ? 'border-amber-300 ring-2 ring-amber-100 shadow-md'
        : 'border-gray-200'
        }`}
      data-testid={`tech-card-${tech.id}`}
    >
      {/* Image or Icon Header */}
      <div className="relative">
        {imageUrl ? (
          <div className="aspect-video bg-gray-100 overflow-hidden">
            <img
              src={imageUrl}
              alt={tech.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
          </div>
        ) : (
          <div className={`aspect-video flex items-center justify-center ${isPrioritized ? 'bg-gradient-to-br from-amber-50 to-amber-100' : 'bg-gradient-to-br from-[#F4F8EF] to-[#E8F0E4]'
            }`}>
            <CategoryIcon className={`w-16 h-16 ${isPrioritized ? 'text-amber-400' : 'text-[#0E6D2B]/30'
              }`} />
          </div>
        )}

        {/* Priority Badge */}
        {isPrioritized && (
          <div className="absolute top-2 left-2">
            <Badge className="bg-amber-500 text-white gap-1">
              <Star className="w-3 h-3 fill-current" /> Featured
            </Badge>
          </div>
        )}

        {/* Category Badge */}
        {tech.category && (
          <div className="absolute top-2 right-2">
            <Badge variant="secondary" className="bg-white/90 text-gray-700 text-[10px]">
              {tech.category}
            </Badge>
          </div>
        )}
      </div>

      <div className="p-4">
        <h3 className="font-semibold text-base text-gray-800 mb-2 group-hover:text-[#0E6D2B] transition-colors line-clamp-2">
          {tech.name}
        </h3>

        {tech.summary && (
          <p className="text-gray-500 text-sm line-clamp-2 mb-2">
            {tech.summary.replace(/<[^>]*>/g, '')}
          </p>
        )}

        {!tech.summary && tech.description && (
          <p className="text-gray-500 text-sm line-clamp-2 mb-2">
            {tech.description.replace(/<[^>]*>/g, '').substring(0, 100)}...
          </p>
        )}

        {tech.year_of_development && (
          <p className="text-xs text-gray-400">
            Developed: {tech.year_of_development}
          </p>
        )}
      </div>
    </div>
  );
});

TechnologyCard.displayName = 'TechnologyCard';

export default function TechnologiesPage() {
  const [technologies, setTechnologies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedTech, setSelectedTech] = useState(null);

  useEffect(() => {
    const fetchTechnologies = async () => {
      try {
        const backendUrl = getBackendURL();
        const response = await fetch(`${backendUrl}/api/technologies`);
        if (response.ok) {
          const data = await response.json();
          setTechnologies(data);
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTechnologies();
  }, []);

  const handleSelectTech = useCallback((tech) => {
    setSelectedTech(tech);
  }, []);

  // Get unique categories
  const categories = [...new Set(technologies.map(t => t.category).filter(Boolean))];

  const filteredTechnologies = technologies.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.description && t.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = categoryFilter === 'all' || t.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Separate prioritized and normal technologies
  const prioritizedTechnologies = filteredTechnologies
    .filter(t => t.priority && t.priority > 0)
    .sort((a, b) => (b.priority || 0) - (a.priority || 0));

  const normalTechnologies = filteredTechnologies
    .filter(t => !t.priority || t.priority === 0)
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main id="main-content" className="py-8 md:py-12">
        <div className="container-cish">
          {/* Page Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-3">
              <FlaskConical className="w-6 h-6 text-[#0E6D2B]" />
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#0E6D2B]">
                Technologies for Commercialization
              </h1>
            </div>
            <p className="text-gray-600 max-w-2xl mx-auto text-sm md:text-base">
              Innovative technologies developed for farmers and industry
            </p>
          </div>

          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8 max-w-2xl mx-auto">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search technologies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white"
                data-testid="tech-search"
              />
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="h-10 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E6D2B] bg-white"
              data-testid="tech-filter"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-4 border-[#0E6D2B] border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : (
            <>
              {/* Featured/Prioritized Technologies Section */}
              {prioritizedTechnologies.length > 0 && (
                <section className="mb-12">
                  <div className="flex items-center gap-2 mb-4">
                    <Star className="w-5 h-5 text-amber-500" />
                    <h2 className="text-xl font-bold text-gray-800">Featured Technologies</h2>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {prioritizedTechnologies.map((tech, index) => (
                      <TechnologyCard
                        key={tech.id || index}
                        tech={tech}
                        isPrioritized={true}
                        onSelect={handleSelectTech}
                      />
                    ))}
                  </div>
                </section>
              )}

              {/* All Technologies Section */}
              {normalTechnologies.length > 0 && (
                <section>
                  <div className="flex items-center gap-2 mb-4">
                    <FlaskConical className="w-5 h-5 text-[#0E6D2B]" />
                    <h2 className="text-xl font-bold text-gray-800">All Technologies</h2>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                    {normalTechnologies.map((tech, index) => (
                      <TechnologyCard
                        key={tech.id || index}
                        tech={tech}
                        isPrioritized={false}
                        onSelect={handleSelectTech}
                      />
                    ))}
                  </div>
                </section>
              )}
            </>
          )}

          {filteredTechnologies.length === 0 && !loading && (
            <div className="text-center py-12 text-gray-500">
              No technologies found matching your search.
            </div>
          )}
        </div>
      </main>

      {/* Technology Detail Modal */}
      <Dialog open={!!selectedTech} onOpenChange={() => setSelectedTech(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedTech && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl text-[#0E6D2B] flex items-center gap-2">
                  {selectedTech.name}
                  {selectedTech.priority > 0 && (
                    <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                  )}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6">
                {/* Image */}
                {getImageUrl(selectedTech.image_url) ? (
                  <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden max-w-md mx-auto">
                    <img
                      src={getImageUrl(selectedTech.image_url)}
                      alt={selectedTech.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="aspect-video bg-gradient-to-br from-[#F4F8EF] to-[#E8F0E4] rounded-lg flex items-center justify-center max-w-md mx-auto">
                    <FlaskConical className="w-24 h-24 text-[#0E6D2B]/20" />
                  </div>
                )}

                {/* Category Badge */}
                {selectedTech.category && (
                  <div className="flex justify-center">
                    <Badge variant="outline" className="border-[#0E6D2B] text-[#0E6D2B]">
                      {selectedTech.category}
                    </Badge>
                  </div>
                )}

                {/* Summary */}
                {selectedTech.summary && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-700 mb-2">Summary</h4>
                    <p className="text-gray-600 text-sm">{selectedTech.summary.replace(/<[^>]*>/g, '')}</p>
                  </div>
                )}

                {/* Description */}
                {selectedTech.description && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-700 mb-2">Description</h4>
                    <div
                      className="text-gray-600 text-sm prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: selectedTech.description }}
                    />
                  </div>
                )}

                {/* Registration & Identification */}
                {(selectedTech.ic_number || selectedTech.ppvfra_registration) && (
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <h4 className="font-semibold text-[#0E6D2B] mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-[#0E6D2B] rounded-full"></span>
                      Registration & Identification
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {selectedTech.ic_number && (
                        <div>
                          <span className="font-medium text-gray-700">IC Number:</span>
                          <p className="text-gray-600">{selectedTech.ic_number}</p>
                        </div>
                      )}
                      {selectedTech.ppvfra_registration && (
                        <div>
                          <span className="font-medium text-gray-700">PPVFRA Registration:</span>
                          <p className="text-gray-600">{selectedTech.ppvfra_registration}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Developer Information */}
                {(selectedTech.inventors || selectedTech.collaborators || selectedTech.maintainer_inventor) && (
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h4 className="font-semibold text-blue-700 mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-blue-700 rounded-full"></span>
                      Developer Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {selectedTech.inventors && (
                        <div>
                          <span className="font-medium text-gray-700">Inventor(s)/Developer(s):</span>
                          <p className="text-gray-600">{selectedTech.inventors}</p>
                        </div>
                      )}
                      {selectedTech.collaborators && (
                        <div>
                          <span className="font-medium text-gray-700">Collaborator(s):</span>
                          <p className="text-gray-600">{selectedTech.collaborators}</p>
                        </div>
                      )}
                      {selectedTech.maintainer_inventor && (
                        <div className="md:col-span-2">
                          <span className="font-medium text-gray-700">Maintainer Inventor:</span>
                          <p className="text-gray-600">{selectedTech.maintainer_inventor}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Year Information */}
                {(selectedTech.year_of_development || selectedTech.year_of_release || selectedTech.year_of_commercialization) && (
                  <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                    <h4 className="font-semibold text-amber-700 mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-amber-700 rounded-full"></span>
                      Timeline
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      {selectedTech.year_of_development && (
                        <div>
                          <span className="font-medium text-gray-700">Year of Development:</span>
                          <p className="text-gray-600">{selectedTech.year_of_development}</p>
                        </div>
                      )}
                      {selectedTech.year_of_release && (
                        <div>
                          <span className="font-medium text-gray-700">Year of Release:</span>
                          <p className="text-gray-600">{selectedTech.year_of_release}</p>
                        </div>
                      )}
                      {selectedTech.year_of_commercialization && (
                        <div>
                          <span className="font-medium text-gray-700">Year of Commercialization:</span>
                          <p className="text-gray-600">{selectedTech.year_of_commercialization}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Licensing Information */}
                {(selectedTech.nature_of_license || selectedTech.licensing_territory || selectedTech.license_fee || selectedTech.duration || selectedTech.target_customers || selectedTech.royalty) && (
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <h4 className="font-semibold text-purple-700 mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-purple-700 rounded-full"></span>
                      Licensing Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {selectedTech.nature_of_license && (
                        <div>
                          <span className="font-medium text-gray-700">Nature of License:</span>
                          <p className="text-gray-600">{selectedTech.nature_of_license}</p>
                        </div>
                      )}
                      {selectedTech.licensing_territory && (
                        <div>
                          <span className="font-medium text-gray-700">Licensing Territory:</span>
                          <p className="text-gray-600">{selectedTech.licensing_territory}</p>
                        </div>
                      )}
                      {selectedTech.duration && (
                        <div>
                          <span className="font-medium text-gray-700">Duration:</span>
                          <p className="text-gray-600">{selectedTech.duration}</p>
                        </div>
                      )}
                      {selectedTech.license_fee && (
                        <div>
                          <span className="font-medium text-gray-700">License Fee:</span>
                          <p className="text-gray-600">{selectedTech.license_fee}</p>
                        </div>
                      )}
                      {selectedTech.target_customers && (
                        <div>
                          <span className="font-medium text-gray-700">Target Customers:</span>
                          <p className="text-gray-600">{selectedTech.target_customers}</p>
                        </div>
                      )}
                      {selectedTech.royalty && (
                        <div>
                          <span className="font-medium text-gray-700">Royalty:</span>
                          <p className="text-gray-600">{selectedTech.royalty}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
